// Top-level dispatch from PlayerState → which screen to render. Mirrors the
// WetookDisplayScreen()/when-block in android/.../ui/MainActivity.kt so the
// two players go through the same decision tree for the same backend data.

import { useEffect, useRef, useState } from 'react'
import BrandHeader from './components/BrandHeader'
import ContentDisplay from './components/ContentDisplay'
import DeactivatedScreen from './components/DeactivatedScreen'
import ErrorScreen from './components/ErrorScreen'
import FullscreenPrompt from './components/FullscreenPrompt'
import LicenseFallbackScreen from './components/LicenseFallbackScreen'
import LoadingScreen from './components/LoadingScreen'
import PingOverlay from './components/PingOverlay'
import StopScreenOverlay from './components/StopScreenOverlay'
import CacheExpiredScreen from './components/CacheExpiredScreen'
import PostSetupLoader from './components/PostSetupLoader'
import PublishingLoader from './components/PublishingLoader'
import ScreenRemovedScreen from './components/ScreenRemovedScreen'
import SetupScreen from './components/SetupScreen'
import SettingsDrawer from './components/SettingsDrawer'
import WaitingScreen from './components/WaitingScreen'
import WifiBadge from './components/WifiBadge'
import { usePlayerState } from './state/usePlayerState'
import { useBackgroundAudio } from './state/useBackgroundAudio'
import { useAudioUnlock } from './state/useAudioUnlock'
import { useOperatorMenu } from './state/useOperatorMenu'

const WIFI_BADGE_VISIBLE_MS = 3_500
// Max time the pulsing-logo loader sits on screen after pairing succeeds.
// We hide it sooner if content arrives first; the cap exists so the loader
// never stays up forever when the dashboard hasn't assigned anything yet.
const POST_SETUP_LOADER_MAX_MS = 1_800
const HOTSPOT_HOLD_MS = 700
// 3 simultaneous fingers — see OperatorHotspot for why this replaced a
// single-finger long-press.
const HOTSPOT_POINTERS = 3

// Tracks the live viewport width / height so the rotation stage can size
// itself to the swapped axes when the orientation type doesn't match the
// physical viewport. Re-evaluates on resize / device orientation change so
// a screen that's physically rotated updates without a reload.
function useViewportSize(): { w: number; h: number } {
  const read = () => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  })
  const [size, setSize] = useState(read)
  useEffect(() => {
    const onResize = () => setSize(read())
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])
  return size
}

export default function App() {
  const { state, fetchContent, applyPendingContent, reportError, reportRetry } = usePlayerState()

  // Operator drawer — 3-finger hold on the corner hotspot (OperatorHotspot
  // below) or press `m` (the web stand-ins for the Android multi-finger
  // gesture / remote MENU key). Mirrors SettingsDrawer.kt.
  const operatorMenu = useOperatorMenu()

  // Wi-Fi badge: show for 3.5s every time isOnline flips. Same UX as the
  // Compose AnimatedVisibility-with-delay loop in MainActivity.kt.
  const [badgeVisible, setBadgeVisible] = useState(false)
  const [badgeIsOnline, setBadgeIsOnline] = useState(true)
  const prevOnlineRef = useRef<boolean | null>(null)
  const badgeTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const current = state.isOnline
    if (current === null) return
    if (prevOnlineRef.current !== null && current !== prevOnlineRef.current) {
      setBadgeIsOnline(current)
      setBadgeVisible(true)
      if (badgeTimerRef.current !== null) window.clearTimeout(badgeTimerRef.current)
      badgeTimerRef.current = window.setTimeout(() => setBadgeVisible(false), WIFI_BADGE_VISIBLE_MS)
    }
    prevOnlineRef.current = current
    return () => {
      if (badgeTimerRef.current !== null) {
        window.clearTimeout(badgeTimerRef.current)
        badgeTimerRef.current = null
      }
    }
  }, [state.isOnline])

  // Post-setup loader: when the screen transitions from unpaired → paired
  // we briefly cover whatever the next state would be (content, waiting,
  // empty) with a pulsing-logo loader. This stops the player from flashing
  // the "Waiting for content" or "No content" screen during the dashboard
  // handoff. No reload — pure in-app transition.
  const [showPostSetup, setShowPostSetup] = useState(false)
  const prevPairedRef = useRef<boolean | null>(null)
  const postSetupTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const prev = prevPairedRef.current
    const now  = state.isPaired
    if (prev === false && now === true) {
      setShowPostSetup(true)
      if (postSetupTimerRef.current !== null) window.clearTimeout(postSetupTimerRef.current)
      postSetupTimerRef.current = window.setTimeout(
        () => setShowPostSetup(false),
        POST_SETUP_LOADER_MAX_MS,
      )
    }
    prevPairedRef.current = now
    return () => {
      if (postSetupTimerRef.current !== null) {
        window.clearTimeout(postSetupTimerRef.current)
        postSetupTimerRef.current = null
      }
    }
  }, [state.isPaired])

  // Hide the loader as soon as we have content to show — no point keeping
  // the pulsing logo up once the carousel is ready to take over. A zoned
  // layout keeps `items` empty, so count its zones' items too.
  useEffect(() => {
    const c = state.content
    const layoutItems = c?.layout?.zones.reduce((n, z) => n + z.items.length, 0) ?? 0
    const flatItems = c?.items?.length ?? 0
    if (showPostSetup && c !== null && (flatItems > 0 || layoutItems > 0)) {
      setShowPostSetup(false)
    }
  }, [showPostSetup, state.content])

  // Content branch needs the full-bleed canvas; state-machine branches sit in
  // the centered flex container. Splitting them avoids forcing every state
  // screen to position itself absolutely.
  //
  // Enterprise license-fallback (see LicenseFallbackScreen) is its OWN
  // full-bleed branch, checked ahead of showingContent: the response that
  // carries fallback_image_url is a real (non-null) ContentResponse with
  // empty items, so showingContent would otherwise be true and hand it to
  // ContentDisplay, which has nothing to render from an empty items array.
  const showingLicenseFallback = !!state.content?.fallback_image_url
    && !state.screenRemoved
    && !showPostSetup
  const showingContent = state.content !== null
    && !state.screenRemoved
    && !showPostSetup
    && !showingLicenseFallback

  // ── Stop-screen attract overlay ────────────────────────────────────────
  // `covered` = the attract overlay is showing (initial state); a touch flips
  // it to revealed, and after dwell_seconds of no touch it re-covers. All
  // local, so it works offline. While covering, the underlying ContentDisplay
  // is paused so only the overlay's video decodes.
  const stop = state.content?.stop_screen ?? null
  const stopHasItems = !!stop && (stop.items?.length ?? 0) > 0
  const [stopCovered, setStopCovered] = useState(true)
  const [revealNonce, setRevealNonce] = useState(0)
  const stopIdRef = useRef<number | null>(null)
  useEffect(() => {
    const id = stop?.id ?? null
    if (id !== stopIdRef.current) {
      stopIdRef.current = id
      setStopCovered(true)   // a new (or removed) stop screen resets to attract
    }
  }, [stop?.id])
  useEffect(() => {
    if (!stopHasItems || stopCovered) return
    const secs = Math.max(1, stop?.dwell_seconds ?? 30)
    const t = window.setTimeout(() => setStopCovered(true), secs * 1000)
    return () => window.clearTimeout(t)
  }, [stopHasItems, stopCovered, revealNonce, stop?.dwell_seconds])

  // Browsers block non-muted media playback before any user gesture, so we
  // start videos muted and the background-audio hook silent. Once any
  // pointer/touch/key event lands (e.g. the FullscreenPrompt button), this
  // flips true and both surfaces re-evaluate to honor audio_unmuted.
  const audioUnlocked = useAudioUnlock()

  // Whether the fullscreen prompt overlay is actually on screen. The state
  // screens reserve bottom space for it (see below) so on short/portrait
  // viewports the prompt sits below the pairing steps instead of covering
  // them; when it hides (fullscreen / installed PWA) the space is reclaimed.
  const [fsPromptVisible, setFsPromptVisible] = useState(false)
  // Exact reserved-space match for the prompt's real (measured) height —
  // see FullscreenPrompt's onHeightChange. A fixed guess here either wasted
  // room or, on short viewports, reserved MORE than the viewport had to
  // spare and forced the state screens into an unwanted scrollbar.
  const [fsPromptHeight, setFsPromptHeight] = useState(0)

  // Drive background music in parallel with the visuals. The hook honors
  // audio_loop_all (sequence override) and per-track loop, gated on
  // audio_unmuted + whether we're actually painting content right now.
  useBackgroundAudio(state.content, showingContent, audioUnlocked)

  // Two-axis orientation. `orientationType` decides the content aspect
  // ratio (16:9 for horizontal, 9:16 for vertical) and `rotationDegrees`
  // rotates the stage at 0 / 90 / 180 / 270 around centre. The pair maps
  // 1:1 onto the Android player's setRequestedOrientation + Compose
  // letterboxing for the same payload, so both renderers stay in sync.
  const orientationType = state.orientationType || 'horizontal'
  const rotationDeg     = state.rotationDegrees || 0
  const { w: vw, h: vh } = useViewportSize()
  const viewportHorizontal = vw >= vh
  const contentHorizontal  = orientationType === 'horizontal'
  // A 90°/270° rotation compensates a panel that can't rotate itself — a TV
  // mounted sideways still reports landscape dimensions, so we spin the
  // stage to line the content up with the physical long axis. A viewport
  // that ALREADY matches the content orientation (a phone/tablet held
  // upright showing a vertical screen) has done that rotation physically;
  // applying it again would lay the content sideways inside a shrunken
  // stage. Ignore the swap there and let the content render upright.
  const effRotationDeg =
    (rotationDeg === 90 || rotationDeg === 270) && contentHorizontal === viewportHorizontal
      ? 0
      : rotationDeg
  // The rotation swaps the viewport's effective width/height at 90° / 270°.
  // We size the stage to the largest target-ratio rectangle that fits in
  // the post-rotation viewport so vertical content on a horizontal kiosk
  // renders as a centred 9:16 strip (the "phone-on-TV" look) without
  // stretching, then any 90° / 270° rotation lets the same content fill
  // the long axis when the screen is mounted sideways.
  const rotatedSwapsAxes = effRotationDeg === 90 || effRotationDeg === 270
  const availW = rotatedSwapsAxes ? vh : vw
  const availH = rotatedSwapsAxes ? vw : vh
  const targetRatio = contentHorizontal ? 16 / 9 : 9 / 16
  const fitsByWidth = availH > 0 && availW / availH <= targetRatio
  const stageW = fitsByWidth ? availW : Math.round(availH * targetRatio)
  const stageH = fitsByWidth ? Math.round(availW / targetRatio) : availH
  // If content and viewport already agree on orientation AND there's no
  // effective rotation, we can let the stage fill the viewport edge-to-edge
  // with no centring math (a vertical screen on a portrait phone fills the
  // whole phone even when the panel isn't exactly 9:16). Every other
  // combination gets a centred + rotated stage.
  const needsCustomStage =
    contentHorizontal !== viewportHorizontal
    || effRotationDeg !== 0
  const stageStyle: React.CSSProperties = needsCustomStage
    ? {
        width:  `${stageW}px`,
        height: `${stageH}px`,
        top:    '50%',
        left:   '50%',
        transform: `translate(-50%, -50%) rotate(${effRotationDeg}deg)`,
        transformOrigin: 'center center',
      }
    : {
        inset: 0,
        transformOrigin: 'center center',
      }

  return (
    <div className="relative w-full h-full bg-bg text-white overflow-hidden">
      {/* Rotated content stage. Only the carousel/state screen rotates;
          the FullscreenPrompt, ping overlay, and Wi-Fi badge sit on the
          un-rotated layer so they stay reachable in their natural
          orientation (the operator's tap-to-fullscreen has to land where
          the browser chrome actually is). */}
      <div className="absolute" style={stageStyle}>
        {showPostSetup ? (
          <div className="flex items-center justify-center w-full h-full">
            <PostSetupLoader />
          </div>
        ) : showingLicenseFallback ? (
          <LicenseFallbackScreen url={state.content!.fallback_image_url!} />
        ) : showingContent ? (
          <>
            <ContentDisplay
              response={state.content!}
              orientationType={orientationType}
              isOnline={state.isOnline}
              audioUnlocked={audioUnlocked}
              paused={stopHasItems && stopCovered}
              onError={reportError}
              onRetry={reportRetry}
              onItemAdvanced={applyPendingContent}
            />
            {stop && stopHasItems && (
              <StopScreenOverlay
                baseResponse={state.content!}
                stopScreen={stop}
                orientationType={orientationType}
                isOnline={state.isOnline}
                audioUnlocked={audioUnlocked}
                covered={stopCovered}
                onDismiss={() => setStopCovered(false)}
                onRestartDwell={() => setRevealNonce(n => n + 1)}
                onError={reportError}
                onRetry={reportRetry}
              />
            )}
          </>
        ) : state.loading ? (
          <div className="flex items-center justify-center w-full h-full">
            <LoadingScreen />
          </div>
        ) : (
          // Post-loading state screens: top-anchored W mark + centered content.
          // Header is the sole brand element here — the inline "M" tiles that
          // used to sit above each screen's title have been removed.
          // While the fullscreen prompt is up, the centering area ends above
          // it (padding = the prompt's actual measured height, reported via
          // onHeightChange) so the setup steps and the prompt share the page
          // instead of overlapping — and short viewports don't lose more
          // room than the prompt genuinely needs.
          <div className="flex flex-col w-full h-full">
            <BrandHeader />
            {/* The state screen fills the space between the header and the
                fullscreen prompt and centers within the FULL available height
                (min-h-full on the inner wrapper), so on a short phone the
                pairing code + steps use the vertical room instead of clustering
                into a scrolling sub-section with dead space above them.
                Vertical (portrait) screens have ample height, so they expand to
                fill with NO scrollbar; horizontal keeps the scroll fallback for
                genuinely-too-short landscape viewports. */}
            <div
              className={`flex-1 min-h-0 ${
                orientationType === 'vertical' ? 'overflow-hidden' : 'overflow-y-auto'
              }`}
              style={{ paddingBottom: fsPromptVisible ? fsPromptHeight : 0 }}
            >
              <div className="min-h-full h-full flex w-full flex-col items-center justify-center py-2">
                {renderStateScreen(state, fetchContent)}
              </div>
            </div>
          </div>
        )}

        {/* Publishing overlay rides inside the rotated stage so the loader
            covers the rotated content frame, not the un-rotated chrome. */}
        {state.publishing && <PublishingLoader />}
      </div>

      {badgeVisible && (
        <div className="absolute left-4 bottom-5 z-10">
          <WifiBadge isOnline={badgeIsOnline} />
        </div>
      )}

      {state.pingOverlay && (
        <PingOverlay name={state.pingOverlay.name} />
      )}

      {/* Fullscreen prompt: self-hides when running as installed PWA or
          while document.fullscreenElement is set. Also suppressed while
          any loader is on screen so the brand mark stays the focal point
          during the initial fetch / handoff / publish transitions. */}
      <FullscreenPrompt
        hidden={state.loading || state.publishing || showPostSetup}
        suppressed={state.content?.hide_player_controls === true}
        onVisibleChange={setFsPromptVisible}
        onHeightChange={setFsPromptHeight}
      />

      {/* Operator drawer — sits above everything (incl. content + chrome) on
          the un-rotated layer so it's always reachable in natural orientation. */}
      <OperatorHotspot onOpen={() => operatorMenu.setOpen(true)} />

      <SettingsDrawer
        open={operatorMenu.open}
        onClose={() => operatorMenu.setOpen(false)}
        state={state}
        onRefresh={fetchContent}
        hideFullscreen={state.content?.hide_player_controls === true}
      />
    </div>
  )
}

function renderStateScreen(
  state: ReturnType<typeof usePlayerState>['state'],
  fetchContent: () => void,
) {
  if (state.screenRemoved)   return <ScreenRemovedScreen />
  // cacheExpired is checked before loading/error so the player switches
  // to the "lost connection" UI the instant TTL fires or admin Evict
  // arrives, even mid-fetch. Background polling continues, and the App
  // swaps back to ContentDisplay as soon as fresh content lands. License
  // expiry routes through the orphan flow instead (SetupScreen with a
  // fresh pairing code), not via this branch.
  if (state.cacheExpired)    return <CacheExpiredScreen />
  if (state.error !== null)  return <ErrorScreen
                                       message={state.error}
                                       noInternet={state.noInternet}
                                       onRetry={fetchContent}
                                     />
  if (!state.isPaired)       return <SetupScreen pairingCode={state.pairingCode} disconnectCode={state.disconnectCode} />
  if (!state.isActive)       return <DeactivatedScreen />
  return <WaitingScreen />
}

// Touch-device entry point for the operator drawer — requires HOTSPOT_POINTERS
// simultaneous fingers held for HOTSPOT_HOLD_MS. Used to be a one-finger
// long-press, which a customer could — and did — trigger just by pausing a
// finger here, popping the operator drawer open in front of them.
//
// This still has to live in a small, FIXED corner region rather than
// covering the whole screen the way Android's equivalent gesture does: an
// interactive menu renders in a full-bleed <iframe> (HtmlFrameDisplay), a
// separate browsing context whose pointer/touch events never reach this
// parent document at all — there is no window-level listener that can see a
// gesture happening over that content, on the web, full stop. The hotspot
// only works today because it's a real DOM element physically stacked above
// the iframe in this one corner (ordinary paint order), not because it's
// "listening everywhere". So the safest available fix here is the same
// fix as Android — go from 1 finger to 3 — applied to the one region that
// can actually receive the touch. Sized well beyond the old 72px to
// comfortably fit three real fingertips at once (3 fingers cannot
// physically land inside a 72px box).
const HOTSPOT_SIZE_PX = 200

function OperatorHotspot({ onOpen }: { onOpen: () => void }) {
  const timerRef = useRef<number | null>(null)
  // Pointer IDs currently down inside the hotspot — the hold timer only
  // arms once HOTSPOT_POINTERS of them are down together, and disarms the
  // instant any one of them lifts (all fingers must stay down the whole time).
  const activePointers = useRef<Set<number>>(new Set())

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const releasePointer = (id: number) => {
    activePointers.current.delete(id)
    if (activePointers.current.size < HOTSPOT_POINTERS) clearTimer()
  }

  return (
    <button
      type="button"
      aria-label="Open operator menu"
      className="absolute left-0 top-0 z-30 opacity-0 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      style={{ height: HOTSPOT_SIZE_PX, width: HOTSPOT_SIZE_PX }}
      onPointerDown={(e) => {
        activePointers.current.add(e.pointerId)
        if (activePointers.current.size === HOTSPOT_POINTERS) {
          clearTimer()
          timerRef.current = window.setTimeout(() => {
            timerRef.current = null
            onOpen()
          }, HOTSPOT_HOLD_MS)
        }
      }}
      onPointerUp={(e) => releasePointer(e.pointerId)}
      onPointerCancel={(e) => releasePointer(e.pointerId)}
      onPointerLeave={(e) => releasePointer(e.pointerId)}
      onContextMenu={(e) => e.preventDefault()}
    />
  )
}
